/**
 * SpiralIndicator
 *
 * A true Archimedean spiral that draws inward as phases progress.
 * 1.5 turns from outer rim (arrival) to centre (the deepest point).
 * Phase 0 = nothing drawn, just the ghost path and start dot.
 * Phase 6 = full spiral drawn to the centre.
 *
 * Not a progress bar. A path being walked inward.
 */

interface SpiralIndicatorProps {
  phase: number  // 0–6
  size?: number
}

const TURNS = 1.5
const PATH_LENGTH = 1000  // normalised — lets us use simple dashoffset math

/** SVG polyline approximation of an Archimedean spiral (inward). */
function buildSpiralPath(
  cx: number, cy: number,
  rMax: number, rMin: number,
  turns: number,
  numPoints = 320
): string {
  const thetaMax = turns * 2 * Math.PI
  const parts: string[] = []
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints
    const theta = t * thetaMax
    // r decreases linearly from rMax (outer) to rMin (inner)
    const r = rMax + (rMin - rMax) * t
    const x = cx + r * Math.cos(theta - Math.PI / 2)  // start at 12 o'clock
    const y = cy + r * Math.sin(theta - Math.PI / 2)
    parts.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`)
  }
  return parts.join(' ')
}

/** XY position at fraction t along the spiral. */
function spiralPoint(
  cx: number, cy: number,
  rMax: number, rMin: number,
  turns: number,
  t: number
): [number, number] {
  const theta = t * turns * 2 * Math.PI
  const r = rMax + (rMin - rMax) * t
  return [
    cx + r * Math.cos(theta - Math.PI / 2),
    cy + r * Math.sin(theta - Math.PI / 2),
  ]
}

export default function SpiralIndicator({ phase, size = 48 }: SpiralIndicatorProps) {
  const cx = size / 2
  const cy = size / 2
  const rMax = size * 0.40    // outer radius — near the edge
  const rMin = size * 0.07    // inner radius — near the centre

  const fraction = Math.min(Math.max(phase / 6, 0), 1)
  const dashOffset = PATH_LENGTH * (1 - fraction)

  const path = buildSpiralPath(cx, cy, rMax, rMin, TURNS)
  const [startX, startY] = spiralPoint(cx, cy, rMax, rMin, TURNS, 0)
  const [dotX, dotY] = spiralPoint(cx, cy, rMax, rMin, TURNS, fraction)

  const gradientId = `sg-${size}`

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#9B72C0" />
          <stop offset="100%" stopColor="#D45D87" />
        </linearGradient>
      </defs>

      {/* Ghost spiral — full path, very faint, shows where you're going */}
      <path
        d={path}
        fill="none"
        stroke="#D8C8E8"
        strokeWidth={size * 0.042}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.65}
        pathLength={PATH_LENGTH}
        strokeDasharray={`3 ${PATH_LENGTH * 0.018}`}
      />

      {/* Active spiral — drawn from outer edge inward as phases complete */}
      <path
        d={path}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth={size * 0.082}
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength={PATH_LENGTH}
        strokeDasharray={`${PATH_LENGTH} ${PATH_LENGTH}`}
        strokeDashoffset={phase === 0 ? PATH_LENGTH : dashOffset}
        style={{
          transition: 'stroke-dashoffset 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />

      {/* Start dot — outer tip, always visible */}
      <circle
        cx={startX}
        cy={startY}
        r={size * 0.038}
        fill="#C4B5D4"
      />

      {/* Current position dot — moves inward along spiral */}
      {phase > 0 && (
        <circle
          cx={dotX}
          cy={dotY}
          r={size * 0.075}
          fill="#9B72C0"
          style={{
            filter: `drop-shadow(0 0 ${size * 0.06}px rgba(155,114,192,0.55))`,
          }}
        />
      )}
    </svg>
  )
}
